import { useCallback, useEffect, useState } from 'react'
import { BreakpointType, FullScaleType, useTheme } from 'styled-components'

const SCREEN_SCALE_ORDER: FullScaleType[] = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']

export function useScreenMatch() {
	const theme = useTheme()
	const [matches, setMatches] = useState<Record<FullScaleType, boolean>>({
		xxs: false,
		xs: false,
		sm: false,
		md: false,
		lg: false,
		xl: false,
		xxl: false,
	})

	useEffect(() => {
		const mediaQueries = SCREEN_SCALE_ORDER.reduce((acc, key, index) => {
			const scaleKey = key as FullScaleType
			const breakpointKey = key as BreakpointType
			const query = [
				index > 0 && `(min-width: ${theme.breakpoints[breakpointKey]}px)`,
				index < SCREEN_SCALE_ORDER.length - 1 &&
					`(max-width: ${theme.breakpoints[SCREEN_SCALE_ORDER[index + 1] as BreakpointType] - 1}px)`,
			]
				.filter(Boolean)
				.join(' and ')
			acc[scaleKey] = window.matchMedia(query)
			return acc
		}, {} as Record<FullScaleType, MediaQueryList>)

		// Set initial values
		setMatches(
			SCREEN_SCALE_ORDER.reduce(
				(acc, key) => ({
					...acc,
					[key]: mediaQueries[key].matches,
				}),
				{} as Record<FullScaleType, boolean>
			)
		)

		// Subscribe to media query changes
		const listeners = SCREEN_SCALE_ORDER.reduce((acc, key) => {
			acc[key] = (e: MediaQueryListEvent) =>
				setMatches(prevMatches => ({
					...prevMatches,
					[key]: e.matches,
				}))

			mediaQueries[key]?.addEventListener?.('change', acc[key])
			return acc
		}, {} as Record<FullScaleType, (e: MediaQueryListEvent) => void>)

		return () => {
			SCREEN_SCALE_ORDER.forEach(key => mediaQueries[key]?.removeEventListener?.('change', listeners[key]))
		}
	}, [theme])

	const eq = useCallback((screen: FullScaleType) => matches[screen], [matches])

	const lte = useCallback(
		(screen: FullScaleType) => {
			const index = SCREEN_SCALE_ORDER.indexOf(screen)
			for (let i = index; i >= 0; i--) {
				if (matches[SCREEN_SCALE_ORDER[i]]) {
					return true
				}
			}
			return false
		},
		[matches]
	)

	const gte = useCallback(
		(screen: FullScaleType) => {
			const index = SCREEN_SCALE_ORDER.indexOf(screen)
			for (let i = index; i < SCREEN_SCALE_ORDER.length; i++) {
				if (matches[SCREEN_SCALE_ORDER[i]]) {
					return true
				}
			}
			return false
		},
		[matches]
	)

	const between = useCallback((min: FullScaleType, max: FullScaleType) => gte(min) && lte(max), [gte, lte])

	const map = useCallback(
		<T>(values: Record<'xxs', T> & Partial<Record<BreakpointType, T>>): T => {
			const size = SCREEN_SCALE_ORDER.find(key => matches[key])
			let index = size ? SCREEN_SCALE_ORDER.indexOf(size) : -1
			while (!values.hasOwnProperty(SCREEN_SCALE_ORDER[index]) && index >= 0) {
				index--
			}
			return index > -1 ? (values[SCREEN_SCALE_ORDER[index]] as T) : values.xxs
		},
		[matches]
	)

	return {
		eq,
		gte,
		lte,
		between,
		map,
	}
}
